import { useEffect } from "react";

/**
 * Hook to handle keyboard shortcuts for workspace
 * @param shortcuts - The shortcuts to handle
 * @returns void
 */

type Shortcut = {
  id: string;
  keys: string[];
  onKeyPressed: (id: string) => void;
};

export function useKeyboard(shortcut: Shortcut) {
  function isInputElement(element: Element | null): boolean {
    if (!element) return false;
    return (
      element.tagName === "INPUT" ||
      element.tagName === "TEXTAREA" ||
      element.getAttribute("contenteditable") === "true"
    );
  }

  function matchesKeyCombination(
    event: KeyboardEvent,
    keyString: string
  ): boolean {
    const parts = keyString.toLowerCase().split("+");
    const key = parts[parts.length - 1].trim();
    const modifiers = parts.slice(0, -1);

    if (event.key.toLowerCase() !== key) {
      return false;
    }

    const needsCtrl = modifiers.includes("ctrl");
    const needsMeta = modifiers.includes("meta");
    const needsShift = modifiers.includes("shift");
    const needsAlt = modifiers.includes("alt");

    if (needsCtrl && !event.ctrlKey) return false;
    if (needsMeta && !event.metaKey) return false;
    if (needsShift && !event.shiftKey) return false;
    if (needsAlt && !event.altKey) return false;

    if (needsCtrl && event.metaKey) return false;
    if (needsMeta && event.ctrlKey) return false;

    return true;
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const activeElement = document.activeElement;
      const eventMatch = shortcut.keys.some((keyString) =>
        matchesKeyCombination(event, keyString)
      );
      if (eventMatch && !isInputElement(activeElement)) {
        shortcut.onKeyPressed(shortcut.id);
        event.preventDefault();
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [shortcut]);
}
